import { FC } from 'react';

import { resolvePlanPricing, PAYWALL_PLAN_KEYS } from './utils/paywall-pricing';
import type { SanityTierCard } from '@lib/sanity/types';
import type { SubscriptionPlansResponse } from '@hooks/useSubscriptionPlans';

type TierComparisonProps = {
  tierCards: SanityTierCard[];
  plans: SubscriptionPlansResponse;
  currencyCode?: string;
  discountPercent?: number;
  selectedKey: string;
  onSelect: (key: string) => void;
};

const RadioDot: FC<{ selected: boolean }> = ({ selected }) => (
  <span
    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
      selected ? 'border-primary-600' : 'border-neutral-300'
    }`}
    aria-hidden="true"
  >
    {selected && <span className="h-2.5 w-2.5 rounded-full bg-primary-600" />}
  </span>
);

/**
 * The single-screen plan selector (spec §8.2): one tappable row per tier with
 * benefit-led descriptors and live per-month pricing. The recommended tier is
 * elevated with the "Best value" badge and pre-selected by the parent. The
 * shared primary CTA checks out whichever tier is selected here.
 */
const TierComparison: FC<TierComparisonProps> = ({
  tierCards,
  plans,
  currencyCode,
  discountPercent,
  selectedKey,
  onSelect
}) => {
  if (!tierCards || tierCards.length === 0) return null;

  // Order by price ascending. The plan keys are listed cheapest-first, so
  // their index gives a stable price order without waiting on the async
  // pricing to load. Unknown/legacy plans (index -1) sort to the end.
  const priceRank = (plan: SanityTierCard['plan']): number => {
    const index = PAYWALL_PLAN_KEYS.indexOf(plan);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  const ordered = [...tierCards].sort(
    (a, b) => priceRank(a.plan) - priceRank(b.plan)
  );

  return (
    <div
      role="radiogroup"
      aria-label="Choose your plan"
      className="flex flex-col gap-4"
    >
      {ordered.map((card) => {
        const isSelected = card._key === selectedKey;
        const { perMonth, fullPerMonth } = resolvePlanPricing(
          plans,
          card.plan,
          currencyCode,
          discountPercent
        );
        // Equal formatted strings mean the effective discount is 0, so a
        // strike-through would just duplicate the price.
        const showFullPriceStrikethrough = Boolean(
          perMonth && fullPerMonth && fullPerMonth !== perMonth
        );

        return (
          <button
            key={card._key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(card._key)}
            className={`relative flex w-full items-start gap-3 rounded-lg px-4 py-3 text-left transition-colors ${
              isSelected
                ? 'border-[3px] border-primary-600 bg-primary-600/5'
                : 'border border-neutral-300 bg-white hover:border-primary-600/50'
            }`}
          >
            {card.isRecommended && (
              <span className="absolute -top-3 right-4 rounded-full bg-primary-600 px-3 py-1 font-body text-xxs font-bold uppercase tracking-wider text-white">
                {card.bestValueLabel ?? 'Best value'}
              </span>
            )}

            <RadioDot selected={isSelected} />

            <div className="flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-sm font-extrabold text-neutral-800">
                  {card.name}
                </h3>
                {perMonth && (
                  <div className="flex-shrink-0 whitespace-nowrap text-right">
                    {showFullPriceStrikethrough && (
                      <span className="block font-body text-xs text-neutral-500 line-through">
                        {fullPerMonth}/mo
                      </span>
                    )}
                    <span className="font-body text-sm font-bold text-neutral-800">
                      {perMonth}
                    </span>
                    <span className="font-body text-xs text-neutral-500">
                      /mo
                    </span>
                  </div>
                )}
              </div>

              {card.benefitDescriptor && (
                <p className="mt-0.5 font-body text-xs font-bold text-primary-600">
                  {card.benefitDescriptor}
                </p>
              )}

              {card.description && (
                <p className="mt-1 font-body text-xs leading-normal text-neutral-500">
                  {card.description}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default TierComparison;
